import { Box, Link, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect } from 'react';
import { deviceType } from 'react-device-detect';
import ReactGA from 'react-ga4';
import { useDispatch, useSelector } from 'react-redux';
import {
    createSearchParams,
    useNavigate,
    useLocation,
    Link as RouterLink,
    useSearchParams,
} from 'react-router-dom';

import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import AuthLayout from 'views/v2/auth/components/authLayout';

import api from 'api';
import { BackendEventProps } from 'api/models/payload/EventsPayload';
import { Permission } from 'api/models/response/permissions';
import { getFilledForms } from 'api/rest/table_input';
import PERMISSION from 'assets/permissions.json';
import ROLE from 'assets/userRoles.json';
import { PageRoutes } from 'constant';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { useInflight } from 'hooks/useInflight';
import { setAlert } from 'redux/alert';
import { loadApplications } from 'redux/applications/applications-middleware';
import { authUser } from 'redux/auth/auth-middleware';
import { formCounterSlice } from 'redux/formCounter';
import { RootState, AppDispatch } from 'redux/store';
import type { LoginInterface } from 'types';
import { ACCOUNT_HOME, selfServeEntryPath } from 'utils/countries';
import { getScreenNumber } from 'utils/getFormPath_v2';
import { isEmptyObject } from 'utils/isEmptyObject';
import { getLocation } from 'utils/location';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { isValidEmail, loginDefaultValues, loginValidationSchema } from 'utils/validations/auth';

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { purpose } = useSelector((state: RootState) => state.formCounter);
    const { initScreen, setPurpose, set } = formCounterSlice.actions;
    const dispatch = useDispatch<AppDispatch>();
    const {
        role,
        completed_assessment,
        id: userId,
    } = useSelector((state: RootState) => state.user.entity);
    const { permissionsList, loading, permissionsLoaded } = useSelector(
        (state: RootState) => state.permissions
    );
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const { codes, hasSelfServeMarket } = useEnabledCountries();
    const country: string = getLocation().userCountry;
    const [queryParameters] = useSearchParams();

    const from = location.state?.from?.pathname;

    useEffect(() => {
        dispatch(initScreen());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname}`,
            title: 'LoginPage',
        });
    }, []);

    const handleStoreCampaignEventProps = async () => {
        const url_params = window.document.referrer
            ? new URL(window.document.referrer).searchParams
            : null;
        const event_properties: BackendEventProps = {
            country: country,
            referrer: window.document.referrer,
            device_type: deviceType,
            utm_source: queryParameters.get('utm_source') || url_params?.get('utm_source') || '',
            utm_medium: queryParameters.get('utm_medium') || url_params?.get('utm_medium') || '',
            utm_campaign:
                queryParameters.get('utm_campaign') || url_params?.get('utm_campaign') || '',
            utm_content: queryParameters.get('utm_content') || url_params?.get('utm_content') || '',
            user_agent: window.navigator.userAgent,
            anonymous_id: localStorage.ajs_anonymous_id?.replaceAll('"', ''),
        };
        const res = await api.events.user_props_put(event_properties);
        return res.data;
    };

    const setToLastCompleteForm = async () => {
        getFilledForms().then((res) => {
            if (isEmptyObject(res)) {
                return;
            }
            const screenNumber = getScreenNumber(res, purpose);
            dispatch(set(screenNumber));
        });
    };

    const loadPurpose = (data: { purpose: string }) => {
        if (data) {
            dispatch(setPurpose(data.purpose));
        }
    };

    const lastVisitedPage = localStorage.getItem('last_visited_page');

    useEffect(() => {
        if (role && isAuthenticated && permissionsLoaded) {
            handleStoreCampaignEventProps();
            const returnPath =
                typeof from === 'string' && from.startsWith('/visa-options') && !hasSelfServeMarket
                    ? null
                    : from;
            let next = returnPath || selfServeEntryPath(codes) || ACCOUNT_HOME;
            const isAdminOrConsultant = [
                ROLE.superSuperAdmin,
                ROLE.admin,
                ROLE.superAdmin,
                ROLE.consultant,
                ROLE.backofficeAdmin,
                ROLE.backofficeConsultant,
            ].includes(role);

            if (isAdminOrConsultant) {
                if (lastVisitedPage) {
                    const stored =
                        lastVisitedPage.startsWith('/visa-options') && !hasSelfServeMarket
                            ? ACCOUNT_HOME
                            : lastVisitedPage;
                    next = stored === '/' ? '/dashboard' : stored;
                    localStorage.removeItem('last_visited_page');
                } else {
                    next = returnPath || '/dashboard';
                }
            }
            if ([ROLE.externalClient].includes(role)) {
                if (
                    permissionsList.some(
                        (permission: Permission) => permission.name === PERMISSION.EDIT
                    ) &&
                    !completed_assessment
                ) {
                    next = `/visa-process/assessment`;
                } else {
                    // Match previous `/visa-process/1/agreement` redirect, but use
                    // the first application id when the list is available.
                    (async () => {
                        let nextPath = `/visa-process/dashboard`;
                        try {
                            if (userId) {
                                const action = await dispatch(loadApplications(String(userId)));
                                if (loadApplications.fulfilled.match(action)) {
                                    const firstId = action.payload.data?.[0]?.id;
                                    if (firstId) {
                                        nextPath = `/visa-process/${firstId}/agreement`;
                                    }
                                }
                            }
                        } catch {
                            // Keep the previous hardcoded agreement path.
                        }
                        navigate(nextPath, { replace: true });
                    })();
                    return;
                }
            }
            if ([ROLE.completedAssessment].includes(role))
                api.tableInput.fetchCandidateInput(loadPurpose, 'Purpose');
            if ([ROLE.unqualified].includes(role)) {
                api.tableInput.fetchCandidateInput(loadPurpose, 'Purpose');
                next = selfServeEntryPath(codes, 'unqualified') ?? ACCOUNT_HOME;
            }
            if ([ROLE.sales].includes(role)) {
                next = '/dashboard/sales-onboarding';
            }
            if ([ROLE.academicConsultation].includes(role)) {
                api.tableInput.fetchCandidateInput(loadPurpose, 'Purpose');
            } else if (role === ROLE.visaCandidate) {
                api.tableInput.fetchCandidateInput(loadPurpose, 'Purpose');
                setToLastCompleteForm();
                next = '/assessment';
            }

            navigate(next, { replace: true });
        }
        // eslint-disable-next-line
    }, [
        codes,
        from,
        hasSelfServeMarket,
        isAuthenticated,
        loading,
        permissionsList.length,
        permissionsLoaded,
        role,
        userId,
    ]);

    const { pending: signingIn, run: runSignIn } = useInflight(
        async (_signal, values: LoginInterface) => {
            const result = await dispatch(
                authUser({
                    email: values.email.trim().toLowerCase(),
                    password: values.password,
                })
            );

            // Toast from this attempt. `auth.error` stays the same string across
            // retries, so a useEffect on that value only ran the first time.
            if (authUser.rejected.match(result)) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(result.payload ?? result.error),
                        type: 'error',
                    })
                );
                return;
            }
        }
    );

    const handleSubmit = async (values: LoginInterface) => {
        await runSignIn(values);
    };

    return (
        <AuthLayout title="Welcome back" subtitle="Sign in to continue to your account">
            <Formik
                initialValues={loginDefaultValues}
                validationSchema={loginValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, values }) => {
                    const disabled = isSubmitting || signingIn;
                    const email = values.email?.trim() ?? '';
                    const forgotPasswordTo = isValidEmail(email)
                        ? {
                              pathname: PageRoutes.forgotPassword,
                              search: `?${createSearchParams({ email })}`,
                          }
                        : PageRoutes.forgotPassword;
                    return (
                        <Form>
                            <Stack spacing={2}>
                                <FormField
                                    name="email"
                                    label="Email"
                                    type="email"
                                    disabled={disabled}
                                />
                                <Box>
                                    <FormField
                                        name="password"
                                        label="Password"
                                        type="password"
                                        disabled={disabled}
                                    />

                                    <Stack
                                        direction="row"
                                        sx={{ justifyContent: 'flex-end', mt: -0.5 }}
                                    >
                                        <Link
                                            component={RouterLink}
                                            to={forgotPasswordTo}
                                            underline="hover"
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                color: 'text.secondary',
                                                py: 0.5,
                                            }}
                                        >
                                            Forgot your password?
                                        </Link>
                                    </Stack>
                                </Box>

                                <SubmitButton
                                    loading={disabled}
                                    disabled={disabled}
                                    fullWidth
                                    size="large"
                                >
                                    Sign In
                                </SubmitButton>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </AuthLayout>
    );
}
